import React, { useState } from 'react';
import { HelpCircle, Calendar, Users, Filter, Copy, Columns, Clock, Keyboard, CheckCircle, AlertTriangle, XCircle, Target, Zap, PlayCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/core/utils';

interface CalendarTutorialDialogProps {
  className?: string;
  onStartInteractiveTutorial?: () => void;
  trigger?: React.ReactNode; // Optional custom trigger element
}

export const CalendarTutorialDialog: React.FC<CalendarTutorialDialogProps> = ({
  className = "",
  onStartInteractiveTutorial,
  trigger
}) => {
  const [open, setOpen] = useState(false);

  const handleStartTutorial = () => {
    setOpen(false);
    onStartInteractiveTutorial?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-8 w-8 p-0", className)}
            aria-label="View calendar tutorial"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="h-5 w-5" />
            Calendar Tutorial
          </DialogTitle>
          <DialogDescription>
            Everything you need to know about shift scheduling and calendar management
          </DialogDescription>
        </DialogHeader>

        {/* Interactive Tutorial Button */}
        {onStartInteractiveTutorial && (
          <div className="pb-4">
            <Button
              onClick={handleStartTutorial}
              className="w-full"
              size="lg"
            >
              <PlayCircle className="h-5 w-5 mr-2" />
              Start Interactive Tutorial
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Follow a guided walkthrough highlighting each feature, or browse the tabs below for detailed reference
            </p>
          </div>
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">
              <PlayCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="creating" className="text-xs sm:text-sm">
              <Zap className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Creating
            </TabsTrigger>
            <TabsTrigger value="managing" className="text-xs sm:text-sm">
              <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Managing
            </TabsTrigger>
            <TabsTrigger value="shortcuts" className="text-xs sm:text-sm">
              <Keyboard className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Tips
            </TabsTrigger>
            <TabsTrigger value="steps" className="text-xs sm:text-sm">
              <Target className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Steps
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <ScrollArea className="h-[calc(90vh-240px)] pr-4">
              <div className="space-y-6 py-4">

            {/* Getting Started */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <PlayCircle className="h-5 w-5 text-primary" />
                Getting Started
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                The calendar is your central hub for shift scheduling. It provides a visual overview of your entire schedule with color-coded validation to ensure everything is properly set up.
              </p>
            </section>

            {/* Calendar Layout */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Calendar Layout
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                The calendar displays days as columns with time slots running vertically. Each cell represents a specific time period where you can create shifts.
              </p>
              <p className="text-sm text-muted-foreground">
                Shifts are displayed as cards overlaying the time grid, making it easy to see your schedule at a glance.
              </p>
            </section>

            {/* Schedule Validation Status */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Schedule Validation Status
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Each day has a colored border indicating schedule completeness. This is your quick health check:
              </p>

              <div className="space-y-2 ml-4">
                <div className="flex items-start gap-2 text-sm">
                  <div className="h-3 w-3 rounded-full bg-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Green (Complete):</span>
                    <span className="text-muted-foreground"> All shifts assigned, opening hours covered</span>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-sm">
                  <div className="h-3 w-3 rounded-full bg-orange-500 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Orange (Partial):</span>
                    <span className="text-muted-foreground"> Some shifts need employees or hours have gaps</span>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-sm">
                  <div className="h-3 w-3 rounded-full bg-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Red (Incomplete):</span>
                    <span className="text-muted-foreground"> No shifts or multiple issues</span>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-sm">
                  <div className="h-3 w-3 rounded-full bg-gray-500 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Gray (Closed):</span>
                    <span className="text-muted-foreground"> Business closed this day</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-3">
                Use these indicators to quickly identify which days need attention and ensure your schedule is complete before the week begins.
              </p>
            </section>

            {/* Month Navigation */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Month Navigation
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Use the navigation controls at the top to move between months and jump to specific dates:
              </p>
              <div className="space-y-2 ml-4">
                <div className="text-sm">
                  <span className="font-medium">Previous/Next Buttons:</span>
                  <span className="text-muted-foreground"> Navigate one month backward or forward</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">Month Picker:</span>
                  <span className="text-muted-foreground"> Click the month name to open a date picker and jump to any month/year</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">Go to Today:</span>
                  <span className="text-muted-foreground"> Quick button to return to the current date</span>
                </div>
              </div>
            </section>

            {/* Calendar Settings */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Calendar Settings
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Customize your calendar view with these settings (accessed via the settings icon):
              </p>
              <div className="space-y-2 ml-4">
                <div className="text-sm">
                  <span className="font-medium">Hide Past Days:</span>
                  <span className="text-muted-foreground"> Focus only on upcoming schedule by hiding historical days</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">24-Hour Format:</span>
                  <span className="text-muted-foreground"> Switch between 12-hour (AM/PM) and 24-hour time display</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">Display Hours:</span>
                  <span className="text-muted-foreground"> Customize the time range shown on the calendar to match your business hours</span>
                </div>
              </div>
            </section>

              </div>
            </ScrollArea>
          </TabsContent>

          {/* Creating Tab */}
          <TabsContent value="creating">
            <ScrollArea className="h-[calc(90vh-240px)] pr-4">
              <div className="space-y-6 py-4">

            {/* Creating Shifts - Best Approaches */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Creating Shifts - Best Approaches
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                There are three main approaches to creating shifts, each suited for different scenarios:
              </p>

              <div className="space-y-3 ml-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">1. Use Templates (Recommended for Repeating Schedules)</p>
                  <p className="text-sm text-muted-foreground">
                    If your schedule repeats weekly or follows a pattern, create day or shift templates once and apply them across multiple days. This is the fastest approach for consistent schedules.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">2. Create Manually, Then Save as Template</p>
                  <p className="text-sm text-muted-foreground">
                    Build a shift schedule for one day, then save it as a template for future use. Great when you're setting up a new schedule pattern.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">3. Copy from Existing Shifts</p>
                  <p className="text-sm text-muted-foreground">
                    Use the copy/duplicate features to replicate shifts from one day or week to another. Perfect for one-time adjustments or similar but non-repeating schedules.
                  </p>
                </div>
              </div>
            </section>

            {/* Templates System */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Templates System
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Templates help you work efficiently by saving and reusing common shift patterns.
              </p>

              <div className="space-y-3 ml-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Shift Templates</p>
                  <p className="text-sm text-muted-foreground">
                    Save individual shift configurations (time, role, break duration, etc.) for quick reuse. Perfect for common shifts like "Morning Manager" or "Evening Cashier".
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Day Templates</p>
                  <p className="text-sm text-muted-foreground">
                    Save complete daily schedules with multiple shifts. Ideal for repeating patterns like "Monday Schedule" or "Weekend Shift Plan". Apply an entire day's worth of shifts with one click.
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-3">
                To create templates, use the "Save as Template" option when editing shifts, or access the template creation panel from the actions sidebar.
              </p>
            </section>

            {/* Shift Creation - Click & Drag */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Creating Shifts: Click & Drag Method
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Click and drag on any time cell to create a new shift. The time range adjusts automatically as you drag down the calendar.
              </p>
              <p className="text-sm text-muted-foreground">
                This is perfect for creating one-off shifts or when you're first setting up your schedule.
              </p>
            </section>

            {/* Shift Editing */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Editing & Moving Shifts
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Once shifts are created, you have multiple ways to modify them:
              </p>

              <div className="space-y-2 ml-4">
                <div className="text-sm">
                  <span className="font-medium">Click to Edit:</span>
                  <span className="text-muted-foreground"> Click any shift to open the edit panel and modify time, employee, notes, or other details</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Drag Handle:</span>
                  <span className="text-muted-foreground"> Use the three-dot handle at the left of each shift card to move it to different days or times</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Resize:</span>
                  <span className="text-muted-foreground"> Drag the edges of a shift to adjust its duration</span>
                </div>
              </div>
            </section>

            {/* Employee Assignment */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Employee Assignment
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Assigning employees to shifts ensures proper staffing and helps with payroll tracking.
              </p>

              <div className="space-y-2 ml-4">
                <div className="text-sm">
                  <span className="font-medium">From Shift Card:</span>
                  <span className="text-muted-foreground"> Click the employee dropdown on any shift card to assign or change the assigned employee</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Drag from Employee List:</span>
                  <span className="text-muted-foreground"> Open the actions panel, switch to Employees mode, and drag employees onto shifts</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Unassigned Indicator:</span>
                  <span className="text-muted-foreground"> Shifts without employees appear with an orange indicator for easy identification</span>
                </div>
              </div>
            </section>

              </div>
            </ScrollArea>
          </TabsContent>

          {/* Managing Tab */}
          <TabsContent value="managing">
            <ScrollArea className="h-[calc(90vh-240px)] pr-4">
              <div className="space-y-6 py-4">

            {/* Actions Panel */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Columns className="h-5 w-5 text-primary" />
                Actions Panel
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                The actions panel is your command center for efficient scheduling. Toggle it using the Columns button in the header.
              </p>

              <div className="space-y-3 ml-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Employees Mode</p>
                  <p className="text-sm text-muted-foreground">
                    View all employees with their availability status. Drag employees from this list directly onto shifts to assign them quickly.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Shifts Mode</p>
                  <p className="text-sm text-muted-foreground">
                    Access shift templates at the top and bulk operations at the bottom. Create, edit, and apply templates for faster scheduling.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Day Actions Mode</p>
                  <p className="text-sm text-muted-foreground">
                    Work with day templates - drag entire day schedules onto the calendar to apply multiple shifts at once.
                  </p>
                </div>
              </div>
            </section>

            {/* Employee List */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Employee List (Actions Panel)
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                The employee list in the actions panel shows all your employees with real-time availability indicators.
              </p>

              <div className="space-y-2 ml-4">
                <div className="text-sm">
                  <span className="font-medium">Drag to Assign:</span>
                  <span className="text-muted-foreground"> Drag employees directly onto shifts for quick assignment</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Availability Indicators:</span>
                  <span className="text-muted-foreground"> See which employees are available or already have conflicting shifts</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Filter by Role:</span>
                  <span className="text-muted-foreground"> Use role filters to find employees with specific skills or positions</span>
                </div>
              </div>
            </section>

            {/* Copying & Duplicating */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copying & Duplicating Shifts
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Quickly replicate shifts across days or weeks to save time on scheduling.
              </p>

              <div className="space-y-2 ml-4">
                <div className="text-sm">
                  <span className="font-medium">Advanced Copy Dialog:</span>
                  <span className="text-muted-foreground"> Copy multiple shifts at once from one date range to another. Great for replicating weekly schedules.</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Quick Copy Button:</span>
                  <span className="text-muted-foreground"> Use the compact copy button for single-day shift duplication to nearby dates.</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Bulk Operations:</span>
                  <span className="text-muted-foreground"> Access bulk copy and edit features from the actions panel when editing mode is enabled.</span>
                </div>
              </div>
            </section>

            {/* Filtering & Highlighting */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Filter className="h-5 w-5 text-primary" />
                Filtering & Highlighting System
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Use the filter dropdown (funnel icon) in the calendar header to focus on specific employees or shift statuses. Filtered shifts are highlighted while others are dimmed for clarity.
              </p>

              <div className="space-y-3 ml-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Employee Filter</p>
                  <p className="text-sm text-muted-foreground">
                    Select one or multiple employees to highlight only their shifts. Perfect for checking individual schedules, workload distribution, or seeing an employee's weekly hours at a glance.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Status Filter</p>
                  <p className="text-sm text-muted-foreground">
                    Filter by shift status (unassigned, in-progress, completed, confirmed, etc.) to quickly find shifts that need attention or track completion status.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Multi-Select Filtering</p>
                  <p className="text-sm text-muted-foreground">
                    Combine multiple employee and status filters to create highly specific views. For example, show only unassigned shifts for a specific week, or view completed shifts for multiple employees.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Clear All Filters</p>
                  <p className="text-sm text-muted-foreground">
                    Click the X button on the filter dropdown to instantly clear all active filters and return to viewing your full schedule.
                  </p>
                </div>
              </div>
            </section>

              </div>
            </ScrollArea>
          </TabsContent>

          {/* Shortcuts & Tips Tab */}
          <TabsContent value="shortcuts">
            <ScrollArea className="h-[calc(90vh-240px)] pr-4">
              <div className="space-y-6 py-4">

            {/* Keyboard Shortcuts */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Keyboard className="h-5 w-5 text-primary" />
                Keyboard Shortcuts
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Speed up your workflow with these keyboard shortcuts:
              </p>

              <div className="space-y-2 ml-4">
                <div className="flex justify-between items-center text-sm max-w-md">
                  <span className="text-muted-foreground">Cancel template creation</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Esc</kbd>
                </div>

                <div className="flex justify-between items-center text-sm max-w-md">
                  <span className="text-muted-foreground">Navigate between shifts</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Tab</kbd>
                </div>

                <div className="flex justify-between items-center text-sm max-w-md">
                  <span className="text-muted-foreground">Open shift options</span>
                  <div className="flex gap-1">
                    <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Enter</kbd>
                    <span className="text-muted-foreground">or</span>
                    <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Space</kbd>
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm max-w-md">
                  <span className="text-muted-foreground">Move focus between elements</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Arrow Keys</kbd>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-3 ml-4">
                Additional shortcuts are available when editing shifts or creating templates
              </p>
            </section>

            {/* Tips Section */}
            <section className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Pro Tips for Efficient Scheduling
              </h3>
              <ul className="space-y-2 ml-4 text-sm text-muted-foreground list-disc list-inside">
                <li>Check the Schedule Validation indicators daily to catch issues before they become problems</li>
                <li>Create templates for your most common shifts to save significant time on weekly scheduling</li>
                <li>Use the employee filter to quickly review individual workloads and ensure fair distribution</li>
                <li>Use the status filter to find and assign all unassigned shifts at once</li>
                <li>Toggle the actions panel (Columns button) to access powerful bulk operations and templates</li>
                <li>Copy entire weeks when your schedule follows a repeating pattern</li>
                <li>Drag employees from the actions panel directly onto shifts for faster assignment</li>
                <li>Use day templates to apply complete daily schedules with one action</li>
                <li>Combine employee and status filters to create highly specific views</li>
                <li>Take advantage of the calendar settings to customize the display for your business hours</li>
              </ul>
            </section>

            {/* Best Practices */}
            <section className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Best Practices
              </h3>
              <div className="space-y-3 ml-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Plan Ahead</p>
                  <p className="text-sm text-muted-foreground">
                    Create your schedule at least one week in advance to give employees time to plan and reduce last-minute changes.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Use Templates Strategically</p>
                  <p className="text-sm text-muted-foreground">
                    Build a library of templates for different scenarios (busy weekends, quiet weekdays, holiday coverage) to handle any situation quickly.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Review Before Publishing</p>
                  <p className="text-sm text-muted-foreground">
                    Always check for green status indicators and use filters to verify all shifts are properly assigned before sharing the schedule with your team.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">Regular Monitoring</p>
                  <p className="text-sm text-muted-foreground">
                    Check the calendar daily for any changes, time-off requests, or unexpected gaps that need attention.
                  </p>
                </div>
              </div>
            </section>

              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tutorial Steps Debug Tab */}
          <TabsContent value="steps">
            <ScrollArea className="h-[calc(90vh-240px)] pr-4">
              <div className="space-y-4 py-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Complete list of all interactive tutorial steps in execution order. Use this to debug the tutorial flow.
                </div>

                {/* Step 1 */}
                <div className="border-l-4 border-primary pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-primary">Step 1</span>
                    <span className="text-xs text-muted-foreground">Target: calendar-container</span>
                  </div>
                  <h4 className="font-semibold text-sm">Welcome & Calendar Overview</h4>
                  <p className="text-xs text-muted-foreground">Introduces the calendar as the central hub with visual overview and color-coded validation.</p>
                </div>

                {/* Step 2 */}
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-blue-500">Step 2</span>
                    <span className="text-xs text-muted-foreground">Target: time-grid</span>
                  </div>
                  <h4 className="font-semibold text-sm">Calendar Layout</h4>
                  <p className="text-xs text-muted-foreground">Explains the grid layout - days as columns, time slots vertically, shifts as overlay cards.</p>
                </div>

                {/* Step 3 */}
                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-green-500">Step 3</span>
                    <span className="text-xs text-muted-foreground">Target: day-column</span>
                  </div>
                  <h4 className="font-semibold text-sm">Schedule Validation Status</h4>
                  <p className="text-xs text-muted-foreground">Shows colored borders: Green (complete), Orange (partial), Red (incomplete), Gray (closed).</p>
                </div>

                {/* Step 4 */}
                <div className="border-l-4 border-orange-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-orange-500">Step 4</span>
                    <span className="text-xs text-muted-foreground">Target: calendar-container</span>
                  </div>
                  <h4 className="font-semibold text-sm">Creating Shifts - Three Approaches</h4>
                  <p className="text-xs text-muted-foreground">Overview of the 3 main methods: Templates, Manual + Save, Copy existing.</p>
                </div>

                {/* Step 5 */}
                <div className="border-l-4 border-primary pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-primary">Step 5</span>
                    <span className="text-xs text-muted-foreground">Target: time-grid</span>
                  </div>
                  <h4 className="font-semibold text-sm">Method 1: Click & Drag</h4>
                  <p className="text-xs text-muted-foreground">How to create shifts by clicking and dragging on time cells.</p>
                </div>

                {/* Step 6 */}
                <div className="border-l-4 border-pink-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-pink-500">Step 6</span>
                    <span className="text-xs text-muted-foreground">Target: shift-templates</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Actions Panel (Templates Mode)</span>
                  </div>
                  <h4 className="font-semibold text-sm">Method 2: Shift Templates</h4>
                  <p className="text-xs text-muted-foreground">Individual shift configurations saved for quick reuse (e.g., "Morning Manager").</p>
                </div>

                {/* Step 7 */}
                <div className="border-l-4 border-secondary/80 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-secondary">Step 7</span>
                    <span className="text-xs text-muted-foreground">Target: day-templates</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Actions Panel (Day Actions Mode)</span>
                  </div>
                  <h4 className="font-semibold text-sm">Day Templates</h4>
                  <p className="text-xs text-muted-foreground">Complete daily schedules with multiple shifts applied with one drag-and-drop.</p>
                </div>

                {/* Step 8 */}
                <div className="border-l-4 border-indigo-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-indigo-500">Step 8</span>
                    <span className="text-xs text-muted-foreground">Target: actions-panel</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Actions Panel</span>
                  </div>
                  <h4 className="font-semibold text-sm">Creating Your Own Templates</h4>
                  <p className="text-xs text-muted-foreground">How to save templates using "Save as Template" option or template creation panel.</p>
                </div>

                {/* Step 9 */}
                <div className="border-l-4 border-red-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-red-500">Step 9</span>
                    <span className="text-xs text-muted-foreground">Target: calendar-container</span>
                  </div>
                  <h4 className="font-semibold text-sm">Method 3: Copying & Duplicating</h4>
                  <p className="text-xs text-muted-foreground">Advanced copy dialog, quick copy button, and bulk operations for replicating shifts.</p>
                </div>

                {/* Step 10 */}
                <div className="border-l-4 border-gray-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-500">Step 10</span>
                    <span className="text-xs text-muted-foreground">Target: shift-card</span>
                    <span className="text-xs bg-red-100 dark:bg-red-900 px-2 py-0.5 rounded">Skips if no shifts exist</span>
                  </div>
                  <h4 className="font-semibold text-sm">Editing Shifts</h4>
                  <p className="text-xs text-muted-foreground">Click to edit, resize by dragging edges, move by dragging handle.</p>
                </div>

                {/* Step 11 */}
                <div className="border-l-4 border-gray-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-500">Step 11</span>
                    <span className="text-xs text-muted-foreground">Target: shift-drag-handle</span>
                    <span className="text-xs bg-red-100 dark:bg-red-900 px-2 py-0.5 rounded">Skips if no shifts exist</span>
                  </div>
                  <h4 className="font-semibold text-sm">Moving Shifts</h4>
                  <p className="text-xs text-muted-foreground">Use the three-dot drag handle to move shifts to different days/times.</p>
                </div>

                {/* Step 12 */}
                <div className="border-l-4 border-gray-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-500">Step 12</span>
                    <span className="text-xs text-muted-foreground">Target: shift-employee-select</span>
                    <span className="text-xs bg-red-100 dark:bg-red-900 px-2 py-0.5 rounded">Skips if no shifts exist</span>
                  </div>
                  <h4 className="font-semibold text-sm">Employee Assignment</h4>
                  <p className="text-xs text-muted-foreground">Assign employees via dropdown on shift card. Unassigned shifts show orange indicator.</p>
                </div>

                {/* Step 13 */}
                <div className="border-l-4 border-secondary/80 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-secondary">Step 13</span>
                    <span className="text-xs text-muted-foreground">Target: employee-list</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Actions Panel (Employees Mode)</span>
                  </div>
                  <h4 className="font-semibold text-sm">Employee List</h4>
                  <p className="text-xs text-muted-foreground">Drag employees from this list onto shifts. View availability and filter by role.</p>
                </div>

                {/* Step 14 */}
                <div className="border-l-4 border-amber-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-amber-500">Step 14</span>
                    <span className="text-xs text-muted-foreground">Target: filter-dropdown</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Filter Popover</span>
                  </div>
                  <h4 className="font-semibold text-sm">Filtering & Highlighting</h4>
                  <p className="text-xs text-muted-foreground">Overview of filtering system - highlight specific shifts while dimming others.</p>
                </div>

                {/* Step 15 */}
                <div className="border-l-4 border-amber-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-amber-500">Step 15</span>
                    <span className="text-xs text-muted-foreground">Target: filter-dropdown</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Filter still open</span>
                  </div>
                  <h4 className="font-semibold text-sm">Employee Filter</h4>
                  <p className="text-xs text-muted-foreground">Select one or multiple employees to highlight their shifts. View workload distribution.</p>
                </div>

                {/* Step 16 */}
                <div className="border-l-4 border-amber-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-amber-500">Step 16</span>
                    <span className="text-xs text-muted-foreground">Target: filter-dropdown</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Filter still open</span>
                  </div>
                  <h4 className="font-semibold text-sm">Status Filter</h4>
                  <p className="text-xs text-muted-foreground">Filter by status (unassigned, in-progress, completed, confirmed, etc.).</p>
                </div>

                {/* Step 17 */}
                <div className="border-l-4 border-amber-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-amber-500">Step 17</span>
                    <span className="text-xs text-muted-foreground">Target: filter-dropdown</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Filter still open</span>
                  </div>
                  <h4 className="font-semibold text-sm">Multi-Select & Clear</h4>
                  <p className="text-xs text-muted-foreground">Combine filters for specific views. Click X to clear all filters.</p>
                </div>

                {/* Step 18 */}
                <div className="border-l-4 border-lime-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-lime-500">Step 18</span>
                    <span className="text-xs text-muted-foreground">Target: actions-toggle</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Actions Panel</span>
                  </div>
                  <h4 className="font-semibold text-sm">Actions Panel Toggle</h4>
                  <p className="text-xs text-muted-foreground">Columns button toggles the actions panel for editing mode and bulk operations.</p>
                </div>

                {/* Step 19 */}
                <div className="border-l-4 border-emerald-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-emerald-500">Step 19</span>
                    <span className="text-xs text-muted-foreground">Target: settings-button</span>
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 rounded">Auto-opens Settings Popover</span>
                  </div>
                  <h4 className="font-semibold text-sm">Calendar Settings</h4>
                  <p className="text-xs text-muted-foreground">Hide past days, 24-hour format, display hours customization.</p>
                </div>

                {/* Step 20 */}
                <div className="border-l-4 border-sky-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-sky-500">Step 20</span>
                    <span className="text-xs text-muted-foreground">Target: month-nav</span>
                  </div>
                  <h4 className="font-semibold text-sm">Month Navigation</h4>
                  <p className="text-xs text-muted-foreground">Previous/Next buttons, month picker dropdown, go to today button.</p>
                </div>

                {/* Step 21 */}
                <div className="border-l-4 border-violet-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-violet-500">Step 21</span>
                    <span className="text-xs text-muted-foreground">Target: calendar-container</span>
                  </div>
                  <h4 className="font-semibold text-sm">Keyboard Shortcuts</h4>
                  <p className="text-xs text-muted-foreground">Esc to cancel, Tab to navigate, Enter/Space to open, Arrow keys to move.</p>
                </div>

                {/* Step 22 */}
                <div className="border-l-4 border-fuchsia-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-fuchsia-500">Step 22</span>
                    <span className="text-xs text-muted-foreground">Target: calendar-container</span>
                  </div>
                  <h4 className="font-semibold text-sm">Pro Tips</h4>
                  <p className="text-xs text-muted-foreground">Check validation daily, create templates, use filters, toggle actions panel, copy patterns.</p>
                </div>

                {/* Step 23 */}
                <div className="border-l-4 border-rose-500 pl-4 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-rose-500">Step 23</span>
                    <span className="text-xs text-muted-foreground">Target: body</span>
                  </div>
                  <h4 className="font-semibold text-sm">Tutorial Complete!</h4>
                  <p className="text-xs text-muted-foreground">Can restart anytime, access reference docs via help icon, experiment with features.</p>
                </div>

                <div className="mt-6 p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Legend:</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-3 bg-yellow-100 dark:bg-yellow-900 rounded"></div>
                      <span>Auto-opens component/popover</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-3 bg-red-100 dark:bg-red-900 rounded"></div>
                      <span>May skip if element doesn't exist</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-3 bg-primary rounded"></div>
                      <span>Step target and number color</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarTutorialDialog;
